import { useState, useRef, useEffect } from 'react'
import { startCase } from 'lodash'

import config from 'environment'

import { useActionless, useAppSelector } from 'redux/hooks'
import { SET_ACTIVE_STATE } from 'redux/active/actions/types'

import { defaultFilter, Filters, FilterType, useCardFilter } from 'components/filters/LocalTextFilter/useCardFilter'

import Icon from 'components/elements/Icon'
import LocalFilterDropdown from './deckFilterDropdown'
import ContextMenu from 'components/elements/ContextMenu'

import { Option } from 'components/elements/ArchidektDropdown/types'
import { CardType } from 'types/deck'
import { basicKeybindSkips } from 'components/deckPage/modals/KeybindsOverlay'

import styles from './localTextFilter.module.scss'

type Props = {
  header: string
  placeholder: string
  cardsToFilter: CardType[]
  filteredCards: string[]
  setFilteredCards: (cardIds: string[]) => void
  useRedux?: boolean
}

const LocalTextFilter = ({
  cardsToFilter,
  filteredCards,
  setFilteredCards,
  header,
  placeholder,
  useRedux = false,
}: Props) => {
  const inputRef = useRef<any>(null)
  const [setActiveState] = useActionless(SET_ACTIVE_STATE)

  const reduxFilters = useAppSelector(state => state.active.cardFilters)
  const reduxSearchValue = useAppSelector(state => state.active.cardFilterValue)

  const [localFilter, setLocalFilter] = useState(defaultFilter)
  const [localSearchValue, setLocalSearchValue] = useState('')

  const filters = useRedux ? reduxFilters : localFilter
  const setFilters = useRedux
    ? (updatedFilters: Filters) => setActiveState({ cardFilters: updatedFilters })
    : setLocalFilter

  const searchValue = useRedux ? reduxSearchValue : localSearchValue
  const setSearchValue = useRedux ? (value: string) => setActiveState({ cardFilterValue: value }) : setLocalSearchValue

  const [visualSearchValue, setVisualSearchValue] = useState(searchValue)

  useCardFilter(filters, searchValue, cardsToFilter, setFilteredCards)

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const controlKey = config.getOnMac() ? e.metaKey : e.ctrlKey

      if (basicKeybindSkips(e)) return
      if (e.key === '/' && controlKey) {
        inputRef.current.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  useEffect(() => {
    const debouncedSearch = setTimeout(() => setSearchValue(visualSearchValue), 500)

    return () => clearTimeout(debouncedSearch)
  }, [visualSearchValue])

  const handleClearFilters = () => {
    setFilters(defaultFilter)
    setSearchValue('')
    setFilteredCards([])
  }

  const handleFilterSelection = (filterKey: FilterType, filterValue: string | string[]) => {
    const values = Array.isArray(filterValue) ? filterValue : [filterValue]

    const updatedFilters = {
      ...filters,
      [filterKey]: {
        ...filters[filterKey],
        ...values.reduce((acc, value) => ({ ...acc, [value]: !filters[filterKey]?.[value] }), {}),
      },
    }

    setFilters(updatedFilters)
  }

  return (
    <div className={styles.row}>
      <div className={`${styles.container}`}>
        <label className={styles.filterHeader}>
          <span>{header || 'Filter'}</span>
          <MetaInfo
            filters={filters}
            filteredCardQuantity={filteredCards.length}
            onClearFilter={handleClearFilters}
            searchValue={searchValue}
          />
        </label>
        <input
          ref={inputRef}
          className={styles.input}
          autoCorrect="off"
          autoComplete="off"
          onFocus={() => inputRef.current.select()}
          placeholder={placeholder || 'Filter (eg: Sol Ring)'}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setVisualSearchValue(e.target.value)}
          value={visualSearchValue}
        />
        <Icon name="search" className={styles.searchIcon} />
      </div>
      <LocalFilterDropdown
        cardsToFilter={cardsToFilter}
        handleFilterSelection={handleFilterSelection}
        onUpdateFilters={setFilters}
        filters={filters}
      />
    </div>
  )
}

export default LocalTextFilter

type MetaInfoProps = {
  filters: Filters
  searchValue: string
  filteredCardQuantity: number
  onClearFilter: () => void
}

const MetaInfo = ({ filters, searchValue, filteredCardQuantity, onClearFilter }: MetaInfoProps) => {
  const filterValues = Object.values(filters)

  // prettier-ignore
  let filterCount = filterValues.reduce((acc, filter) => (acc += Object.values(filter).reduce((acc, val) => (acc += val ? 1 : 0), 0)), 0)

  if (2 < searchValue.length) filterCount++

  const filterList: { filterKey: FilterType; filterValue: string; label: string }[] = []

  for (const [key, value] of Object.entries(filters)) {
    for (const [subKey, subValue] of Object.entries(value)) {
      if (!subValue) continue

      filterList.push({
        filterKey: key as FilterType,
        filterValue: subKey,
        label: `${startCase(key)}: ${subKey}`,
      })
    }
  }

  if (searchValue.length <= 2 && filterCount < 1) return null

  return (
    <ContextMenu
      openOnLeftClick
      menuClassName={styles.filterListMenu}
      options={[
        { label: 'Clear all', onClick: onClearFilter, icon: 'close' },
        { type: 'spacer' },
        {
          label: `Text Search: ${searchValue}`,
          className: styles.listedFilter,
          hidden: searchValue.length < 2,
        },
        { type: 'spacer', hidden: searchValue.length < 2 || !filterList.length },
        ...filterList.map(
          ({ label }): Option => ({
            label: label,
            className: styles.listedFilter,
          }),
        ),
      ]}>
      <button className={styles.resetFilters}>
        {filterCount} {filterCount > 1 ? 'filters' : 'filter'} (
        {filteredCardQuantity ? `${filteredCardQuantity} cards` : 'No results found'})
        <Icon name="caret down" />
      </button>
    </ContextMenu>
  )
}
